import React, { FC, useEffect, useState, useCallback } from "react";
import CommonLayout from "./CommonLayout";
import { useLocation, useNavigate } from "react-router-dom";
import api from '../../api';
import SimpleImageSlider from "react-simple-image-slider";
import { LinearProgress, Stack, Snackbar, Alert, AlertColor, CircularProgress } from "@mui/material";
import { connect, useDispatch } from "react-redux";
import { updateUserBrand, deleteBrandImage } from "../../redux/reducers/userReducer";
import DeleteConfirmationModal from "components/DeletModal/DeleteConfirmationModal";

export interface PageAttachImagesProps {
  user: any;
}

const PageAttachImages: FC<PageAttachImagesProps> = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const nextHref = `/account#brands`;
  const dispatch = useDispatch();

  const DeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
    </svg>
  );

  const { brand, formData } = location.state || {};

  const [savedFormData, setSavedFormData] = useState(formData);

  const [images, setImages] = useState([]);
  const [isImageLoading, setImageLoading] = useState(false);
  const [isDeleteImageLoading, setDeleteImageLoading] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentIndex, setIndex] = useState(0);
  const [currBrand, setCurrBrand] = useState(null);
  const [sliderKey, setSliderKey] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number, type: 'image' | 'video' } | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState({
    open: false,
    type: 'success' as AlertColor,
    message: ''
  });

  const processImages = useCallback(() => {
    if (currBrand?.images) {
      const imgs = currBrand.images.map((i) => {
        return {
          id: i.id,
          isLoading: false,
          name: i.url,
          caption: i.caption,
          url: `${api.getUploadUrl()}/${i.url}`,
        };
      });
      setImages(imgs);
      setTimeout(() => {
        setSliderKey(prev => prev + 1); // change key to force remount
        if (currentIndex + 1 > imgs.length) {
          setIndex(0);
        }
      }, 100);
    }
  }, [currBrand?.images]);

  useEffect(() => {
    if (formData) {
      setSavedFormData(formData);
    }
  }, [formData]);

  useEffect(() => {
    const brandIndex = user.company.brands.findIndex(b => b.id === brand.id);
    if (brandIndex) {
      setCurrBrand(user?.company?.brands[brandIndex]);
    }
  }, [user?.company?.brands]);

  useEffect(() => {
    setCurrentImage(images && images.length ? images[currentIndex] : null);
  }, [images, currentIndex]);


  useEffect(() => {
    processImages();
  }, [processImages]);

  const isFileExists = (f) => {
    for (let i in images) {
      if (images[i].name == f.name) return true;
    }
    return false;
  }

  const handleOnChange = (e) => {
    let imagesTmp = [].concat(images);
    const uploadedImages = [];

    let files = e.target.files;

    if (files.length + images.length > 8) {
      setOpenSnackbar({ open: true, type: 'error' as AlertColor, message: "You can only upload up to " + (8 - images.length) + " images." });
      e.target.value = "";
      return;
    }

    setImageLoading(true);

    for (let i in files) {
      let file = files[i];
      if (file.name === undefined) return;
      if (isFileExists(file)) return;
      imagesTmp.push({
        isLoading: true,
        name: file.name,
        url: '',
      });

      let formData = new FormData();
      formData.append("file", file);
      formData.append("key", "brand_image");
      formData.append("entity", "brands");
      formData.append("entity_id", brand.id);

      api.uploadAttachment(formData, (res) => {
        uploadedImages.push(res.data);
        if (uploadedImages.length === files.length) {
          handleUploadSuccess(uploadedImages);
          setImageLoading(false);
        }
      });
    }
  }

  const handleArrowClick = (e) => {
    const max = images.length - 1;
    let i = 0;
    if (e) {
      i = currentIndex !== max ? (currentIndex + 1) : 0;
    } else {
      i = currentIndex !== 0 ? (currentIndex - 1) : max;
    }

    setIndex(i);
  };

  const handleBulletClick = (i) => {
    setIndex(i);
  };

  const openDeleteModal = (id: number, type: 'image' | 'video') => {
    setItemToDelete({ id, type });
    setDeleteModalOpen(true);
  };

  const confirmDeleteAttachment = () => {
    if (itemToDelete) {
      setDeleteImageLoading(true);
      api.deleteAttachment(
        itemToDelete.id,
        (res) => {
          dispatch(deleteBrandImage(res.data));
          setDeleteModalOpen(false);
          setDeleteImageLoading(false);
        }
      );
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar({ open: false, type: openSnackbar.type, message: openSnackbar.message });
  };

  const handleUploadSuccess = (files) => {
    let tmp = currBrand.images.concat(files);
    setIndex(tmp.length - 1);
    dispatch(updateUserBrand({
      ...currBrand,
      images: tmp
    }));
  }

  const handleSubmit = () => {
    navigate('/account', {
      state: { activeTab: 'brands', fromCreateBrand: true },
      replace: true
    });
  }

  const handleBackClick = () => {
    navigate('/create-brand', {
      state: {
        formData: savedFormData,
        brand: brand
      }
    });
  };

  return (
    <CommonLayout
      index="02"
      maxIndex={'2'}
      backtHref="/create-brand"
      nextHref={nextHref}
      onBack={handleBackClick}
      onSubmit={handleSubmit}
    >
      <div>
        <h2 className="text-2xl font-semibold">Showcase your brand</h2>
        <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
          A few photos will help attract more agents to your brand
        </span>
      </div>

      <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

      <div>
        <div className="mt-18 flex justify-between">
          <span className="block text-neutral-500 dark:text-neutral-400">
            Pictures of Your Showroom and Team (Max: 8 images)
          </span>
          {currentImage && (
            <div onClick={() => openDeleteModal(currentImage.id, 'image')} className="cursor-pointer">
              <DeleteIcon />
            </div>
          )}
        </div>
        <div className="mt-18">
          <div className="space-y-8">
            <div>
              <div className="mt-5">
                <div className="relative">
                  {images.length > 0 && (
                    <>
                      <SimpleImageSlider
                        key={sliderKey}
                        style={{ width: '100% !important' }}
                        width={'100%'}
                        height={300}
                        images={images}
                        showBullets={!isImageLoading && !isDeleteImageLoading}
                        showNavs={!isImageLoading && !isDeleteImageLoading}
                        onClickNav={handleArrowClick}
                        onClickBullets={handleBulletClick}
                        startIndex={currentIndex}
                      />
                    </>
                  )}
                </div>
              </div>
              {isImageLoading &&
                <div className="flex mt-5">
                  <div className="flex-1"></div>
                  <div className="text-center">
                    <CircularProgress size={100} />
                    <div className="mt-4">Uploading...</div>
                  </div>
                  <div className="flex-1"></div>
                </div>
              }
              {images.length < 8 && !isImageLoading &&
                <div className="mt-5">
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-300 dark:border-neutral-6000 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-neutral-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                      </svg>
                      <div className="flex text-sm text-neutral-6000 dark:text-neutral-300">
                        <label
                          htmlFor="file-upload-brand_image"
                          className="relative cursor-pointer rounded-md font-medium text-primary-6000 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                        >
                          <span>Upload files</span>
                          <input
                            id="file-upload-brand_image"
                            name="files"
                            type="file"
                            multiple={true}
                            accept="image/*"
                            className="sr-only"
                            onChange={handleOnChange}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        PNG, JPG, GIF up to 10MB
                      </p>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDeleteAttachment}
        itemType={itemToDelete?.type || 'image'}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={openSnackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={openSnackbar.type}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {openSnackbar.message}
        </Alert>
      </Snackbar>
    </CommonLayout >
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
});

export default connect(mapStateToProps, { updateUserBrand, deleteBrandImage })(PageAttachImages);

const ImageItem = ({ isLoading, name, url }) => {
  if (url == '')
    return (
      <div className="image-item-box space-y-1 text-center">
        <div>{name}</div>
        {
          isLoading ? <LinearProgress sx={{ bottom: '0' }} /> : null
        }
      </div>
    );
  else
    return (
      <div className="image-item-box space-y-1 text-center" style={{ background: `url("${url}")`, backgroundSize: 'cover', backgroundPosition: 'center center' }}>
      </div>
    );
}